import "@fontsource/cinzel";
import "@fontsource/cinzel/600.css";
import "@fontsource/cinzel/700.css";
import "@fontsource/im-fell-english";
import "@fontsource/im-fell-english/400-italic.css";
import "@fontsource/metamorphous";
import "./style.css";
import pkg from "../package.json";

import { SoundEngine } from "./audio/sound";
import { randomSeed } from "./game/deck";
import { findFoundationMove, findHint, isBlocked, type Hint } from "./game/hints";
import { foundationIndex } from "./game/rules";
import { KlondikeEngine, type ActionResult } from "./game/state";
import { cardId, type GameState, type MoveSource } from "./game/types";
import { SolitaireView, type DropTarget } from "./render/cards3d";
import { TavernScene, type GraphicsQuality } from "./render/scene";
import { LossScreen, VictoryScreen } from "./ui/gameOver";
import { loadGraphicsQuality } from "./ui/graphicsSettings";
import { GameHud } from "./ui/hud";
import { MainMenu } from "./ui/menu";
import { loadSettings, type SolitaireSettings } from "./ui/settings";
import { openSettingsModal } from "./ui/settingsModal";
import { initRotateHint } from "./ui/rotateHint";

/**
 * Controleur principal : assemble le moteur pur (src/game), la scene 3D
 * (src/render), l'UI DOM (src/ui) et l'audio. La vue est pilotee par l'etat :
 * chaque action du joueur passe par le moteur, puis `view.sync` anime la
 * difference — deplacements, retours elastiques et undo suivent le meme chemin.
 */

const viewport = document.getElementById("viewport")!;
const uiRoot = document.getElementById("ui-root")!;

const versionTag = document.createElement("div");
versionTag.id = "version-tag";
versionTag.textContent = `v${pkg.version}`;
document.body.appendChild(versionTag);

initRotateHint();

let settings: SolitaireSettings = loadSettings();

const scene = new TavernScene(viewport, loadGraphicsQuality());
const view = new SolitaireView(scene);
const sound = new SoundEngine();

let engine: KlondikeEngine | null = null;
let currentSeed = randomSeed();
let elapsedSeconds = 0;
let timerRunning = false;
let wasBlocked = false;

window.setInterval(() => {
  if (timerRunning) {
    elapsedSeconds++;
    hud.setTime(elapsedSeconds);
  }
}, 1000);

const onGraphicsChange = (quality: GraphicsQuality): void => scene.setQuality(quality);

const onSettingsChange = (next: SolitaireSettings): void => {
  settings = next;
  hud.setTimerVisible(settings.showTimer);
  view.highlightEnabled = settings.highlight;
};

const hud = new GameHud(uiRoot, {
  onUndo: () => {
    if (!engine || !engine.undo()) return;
    sound.undo();
    refreshHud();
    void view.sync(engine.view).then(() => afterMove());
  },
  onHint: () => {
    if (!engine) return;
    const state = engine.view;
    const hint = findHint(state);
    if (!hint) {
      sound.blocked();
      hud.flash("Aucun coup évident — Annuler ou Nouvelle donne ?", "soft");
      return;
    }
    hud.flash(hint.label, "neutral");
    view.flashHint(hintCardIds(hint, state));
  },
  onAutoComplete: () => void runAutoComplete(),
  onNewGame: () => void startGame(randomSeed()),
  onRestartSame: () => void startGame(currentSeed),
  onOpenSettings: () => openSettingsModal(uiRoot, onGraphicsChange, onSettingsChange),
  onQuitToMenu: () => {
    timerRunning = false;
    view.setInteractive(false);
    hud.hide();
    menu.show();
  },
});
hud.callbacksPausedOpen = () => sound.buttonClick();

const victory = new VictoryScreen(
  uiRoot,
  () => void startGame(randomSeed()),
  () => void startGame(currentSeed),
  () => {
    timerRunning = false;
    hud.hide();
    menu.show();
  },
);

const loss = new LossScreen(
  uiRoot,
  () => void startGame(randomSeed()),
  () => {
    timerRunning = false;
    hud.hide();
    menu.show();
  },
);

const menu = new MainMenu(
  uiRoot,
  () => {
    // Premier geste utilisateur : on peut demarrer l'audio (exigence navigateur).
    sound.ensureStarted();
    sound.buttonClick();
    sound.startAmbience();
    sound.startMusic();
    void startGame(randomSeed());
  },
  onGraphicsChange,
  onSettingsChange,
);

// ------------------------------------------------------------- interactions 3D

view.onPickUp = () => sound.cardPick();
view.onCascadeBounce = () => sound.cascadeBounce();

view.onStockClick = () => {
  if (!engine) return;
  const result = engine.draw();
  if (!result.ok) return;
  if (result.recycled) sound.cardPlace();
  else sound.cardFlip();
  refreshHud();
  void view.sync(engine.view).then(() => afterMove());
};

view.onDrop = (source: MoveSource, target: DropTarget): boolean => {
  if (!engine) return false;
  let result: ActionResult;
  if (target.type === "foundation") {
    result = engine.moveToFoundation(source);
  } else if (source.type === "waste") {
    result = engine.moveWasteToTableau(target.col);
  } else if (source.type === "tableau") {
    result = engine.moveTableauRun(source.col, source.index, target.col);
  } else {
    result = engine.moveFoundationToTableau(source.suit, target.col);
  }
  if (!result.ok) {
    sound.invalidMove();
    return false;
  }
  if (result.toFoundation) sound.foundationPlace();
  else sound.cardPlace();
  if (result.flipped) sound.cardFlip();
  refreshHud();
  void view.sync(engine.view).then(() => afterMove());
  return true;
};

view.onDoubleTap = (source: MoveSource): boolean => {
  if (!engine) return false;
  // Resout la destination : fondation en priorite, sinon la premiere colonne
  // du tableau qui peut l'accueillir. Feedback sonore meme en l'absence de
  // destination, pour que le geste ne reste jamais silencieux.
  const result = engine.resolveDoubleClick(source);
  if (!result.ok) {
    sound.invalidMove();
    return false;
  }
  if (result.toFoundation) sound.foundationPlace();
  else sound.cardPlace();
  if (result.flipped) sound.cardFlip();
  refreshHud();
  void view.sync(engine.view).then(() => afterMove());
  return true;
};

// ------------------------------------------------------------- partie

async function startGame(seed: number): Promise<void> {
  // Les textures de cartes ecrivent du texte : attendre les polices evite
  // de graver la police de repli dans les canvas.
  await document.fonts.ready;
  currentSeed = seed;
  engine = new KlondikeEngine({ seed, drawCount: settings.draw3 ? 3 : 1 });
  wasBlocked = false;
  elapsedSeconds = 0;
  timerRunning = true;

  victory.hide();
  loss.hide();
  menu.hide();
  hud.show();
  hud.setTime(0);
  hud.setTimerVisible(settings.showTimer);
  hud.setActionsEnabled(true);
  view.highlightEnabled = settings.highlight;
  refreshHud();

  sound.cardShuffle();
  await view.deal(engine.view, () => sound.cardDeal());
}

function refreshHud(): void {
  if (!engine) return;
  hud.setMoves(engine.view.moves);
  hud.setUndoEnabled(engine.canUndo);
  hud.setFinishVisible(engine.autoCompletable && !engine.won);
}

/** Apres chaque coup accepte : victoire, auto-complete disponible, blocage. */
function afterMove(): void {
  if (!engine) return;
  if (engine.won) {
    win();
    return;
  }
  const blocked = isBlocked(engine.view);
  if (blocked && !wasBlocked) {
    timerRunning = false;
    sound.blocked();
    loss.show();
  }
  wasBlocked = blocked;
}

/** Fin automatique en cascade : joue tous les coups triviaux vers les fondations. */
async function runAutoComplete(): Promise<void> {
  if (!engine) return;
  view.setInteractive(false);
  hud.setActionsEnabled(false);
  hud.setFinishVisible(false);
  let guard = 60;
  for (;;) {
    const move = findFoundationMove(engine.view);
    if (!move || guard-- <= 0) break;
    const result = engine.moveToFoundation(move.source as MoveSource);
    if (!result.ok) break;
    sound.foundationPlace();
    refreshHud();
    await view.sync(engine.view, { duration: 0.16 });
  }
  hud.setActionsEnabled(true);
  if (engine.won) {
    win();
  } else {
    view.setInteractive(true);
  }
}

function win(): void {
  if (!engine) return;
  timerRunning = false;
  hud.setFinishVisible(false);
  sound.victory();
  view.startVictoryCascade();
  const moves = engine.view.moves;
  const time = elapsedSeconds;
  // Petite respiration : on laisse la cascade s'installer avant le parchemin.
  window.setTimeout(() => victory.show(time, moves), 1700);
}

/** Cartes a surligner pour un indice : la carte source et la carte cible. */
function hintCardIds(hint: Hint, state: GameState): string[] {
  const ids: string[] = [];
  if (hint.source.type === "waste") {
    const top = state.waste[state.waste.length - 1];
    if (top) ids.push(cardId(top));
  } else if (hint.source.type === "tableau") {
    const card = state.tableau[hint.source.col]?.[hint.source.index];
    if (card) ids.push(cardId(card));
  } else if (hint.source.type === "draw") {
    const top = state.stock[state.stock.length - 1];
    if (top) ids.push(cardId(top));
  }
  if (hint.target?.type === "tableau") {
    const pile = state.tableau[hint.target.col];
    const top = pile?.[pile.length - 1];
    if (top) ids.push(cardId(top));
  } else if (hint.target?.type === "foundation") {
    const sourceCard =
      hint.source.type === "waste"
        ? state.waste[state.waste.length - 1]
        : hint.source.type === "tableau"
          ? state.tableau[hint.source.col]?.[hint.source.index]
          : undefined;
    if (sourceCard) {
      const pile = state.foundations[foundationIndex(sourceCard.suit)];
      const top = pile?.[pile.length - 1];
      if (top) ids.push(cardId(top));
    }
  }
  return ids;
}
